import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import Form from 'react-bootstrap/Form'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Badge from 'react-bootstrap/Badge'

import LoadingContext from '../../context/Loading/Loading'
import AuthService from '../../utilities/services/auth.service'
import { getAccessPasses } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import {
  exportData,
  formatPhoneNumber,
  capitalizeString,
  formatTicketLocationValue,
} from '../../utilities/helpers'
import moment from 'moment'

import { PageLoadingContainer } from '../PageLoadingContainer'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'
import { SearchBar } from '../SearchBar'
import { ReportRefreshButton } from '../ReportRefreshButton'

import '../ClubSeatWrapper/report-print.scss'
import './accessPassHoldersReport.scss'

const STATUS_OPTS = [
  { label: 'Active', value: 'active' },
  { label: 'Revoked', value: 'revoked' },
  { label: 'All', value: 'all' },
]

const formatSeat = (pass) => {
  const fallback = pass?.generalAdmission ? 'GA' : ''
  const parts = [
    formatTicketLocationValue(pass?.sectionNumber, fallback),
    formatTicketLocationValue(pass?.rowNumber, fallback),
    formatTicketLocationValue(pass?.seatNumber, fallback),
  ].filter(Boolean)
  return parts.length ? parts.join(' / ') : ''
}

const holderDisplayName = (pass) => {
  const fromHolder = pass?.holder?.fullName
  if (fromHolder) return fromHolder
  const user = pass?.users_permissions_user
  const fromUser = `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
  if (fromUser) return fromUser
  return pass?.name || '—'
}

const holderEmail = (pass) =>
  pass?.holder?.email ||
  pass?.email ||
  pass?.users_permissions_user?.email ||
  '—'

const holderPhone = (pass) =>
  pass?.holder?.phoneNumber ||
  pass?.users_permissions_user?.phoneNumber ||
  pass?.order?.phoneNumber ||
  ''

const toExportRow = (pass) => {
  const name = holderDisplayName(pass)
  const [firstName = '', ...rest] = String(name === '—' ? '' : name)
    .trim()
    .split(/\s+/)
  const lastName = rest.join(' ')

  return {
    pass_name: pass?.name || '',
    check_in_code: pass?.checkInCode || '',
    first_name: firstName,
    last_name: lastName,
    name: name === '—' ? '' : name,
    email: holderEmail(pass) === '—' ? '' : holderEmail(pass),
    phone_number: holderPhone(pass),
    address: pass?.holder?.address || pass?.address || '',
    events: pass?.events?.length || 0,
    seat: formatSeat(pass),
    status: pass?.status || '',
    order_id: pass?.order?.orderId || '',
    issued: pass?.createdAt ? moment(pass.createdAt).format('MMM D, YYYY') : '',
  }
}

export default function AccessPassHoldersReportWrapper({ organization }) {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const org = organization || AuthService.getOrg()?.[0]

  const [passes, setPasses] = useState([])
  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)
  const [status, setStatus] = useState('active')
  const [includeSeasonSeats, setIncludeSeasonSeats] = useState(false)
  const [query, setQuery] = useState('')

  useLayoutEffect(() => {
    const root = document.getElementById('root')
    root?.classList.add('report-page')
    return () => {
      root?.classList.remove('report-page')
    }
  }, [])

  const loadPasses = useCallback(async () => {
    if (!org?.uuid) {
      hideLoading()
      return
    }

    setIsLoadingData(true)
    showLoading()
    try {
      const statusParam = status === 'all' ? '' : status
      const res = await getAccessPasses(org.uuid, { status: statusParam })
      const allPasses = res?.data?.data || []
      setPasses(
        includeSeasonSeats
          ? allPasses
          : allPasses.filter((pass) => pass.type !== 'season_seat')
      )
    } catch (error) {
      console.error('Failed to load access pass holders', error)
      setPasses([])
    } finally {
      setIsLoadingData(false)
      hideLoading()
      completeRefresh()
    }
  }, [org?.uuid, status, includeSeasonSeats, showLoading, hideLoading])

  useEffect(() => {
    loadPasses()
  }, [loadPasses, refreshToken])

  const filteredPasses = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    if (!normalized) return passes

    return passes.filter((pass) => {
      const haystack = [
        pass?.name,
        pass?.checkInCode,
        holderDisplayName(pass),
        holderEmail(pass),
        holderPhone(pass),
        pass?.holder?.address || pass?.address,
        pass?.status,
        pass?.order?.orderId,
        ...(pass?.events || []).map((event) => event?.name),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return haystack.includes(normalized)
    })
  }, [passes, query])

  const handleExport = () => {
    if (!filteredPasses.length) return
    exportData(
      filteredPasses.map(toExportRow),
      'access_pass_holders',
      undefined,
      undefined,
      undefined,
      org?.name
    )
  }

  const handlePrint = () => {
    window.print()
  }

  if (isLoading && !passes.length) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  return (
    <section
      className="spacer-sm access-pass-holders-report"
      id="access-pass-holders-report"
    >
      <header className="section-heading--flex section-header-sm report-page-header">
        <div className="section-heading">
          <h1>Access Pass Holders</h1>
          <p className="section-header-desc">
            Printable roster of access pass holders with profile contact
            details. Export to Excel or print at any time.
          </p>
        </div>
        <Stack direction="horizontal" gap={2} className="report-page-header">
          <Button
            variant="outline-light"
            className="icon-button btn-download form-control-height"
            disabled={isLoadingData || filteredPasses.length === 0}
            onClick={handleExport}
          >
            Excel
          </Button>
          <Button
            variant="outline-light"
            className="icon-button btn-download form-control-height"
            disabled={isLoadingData || filteredPasses.length === 0}
            onClick={handlePrint}
          >
            Print
          </Button>
        </Stack>
      </header>

      <div className="report-page-header mb-4">
        <Stack
          direction="horizontal"
          gap={3}
          className="flex-wrap align-items-end"
        >
          <div className="flex-grow-1" style={{ minWidth: '220px' }}>
            <SearchBar
              getQuery={setQuery}
              placeholder="Search by name, email, code, or phone"
            />
          </div>
          <FloatingLabel controlId="holders-status" label="Status">
            <Form.Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={isLoadingData}
              style={{ minWidth: '140px' }}
            >
              {STATUS_OPTS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
          {/* <Form.Check
            type="switch"
            id="include-season-seats"
            label="Include season seat passes"
            checked={includeSeasonSeats}
            disabled={isLoadingData}
            onChange={(e) => setIncludeSeasonSeats(e.target.checked)}
          /> */}
          <ReportRefreshButton
            onRefresh={refresh}
            isRefreshing={isLoadingData}
            disabled={isLoadingData}
          />
        </Stack>
      </div>

      <div className="print-only-header">
        <h2>{org?.name || 'Organization'}</h2>
        <p>Access Pass Holders Report</p>
        <p className="small">
          Printed {moment().format('MMM D, YYYY h:mm A')} ·{' '}
          {filteredPasses.length} holder
          {filteredPasses.length === 1 ? '' : 's'}
        </p>
      </div>

      {isLoadingData ? (
        <LoadingContainer />
      ) : filteredPasses.length === 0 ? (
        <Card body>
          <EmptyContainer style="center lg report">
            <p>
              {passes.length === 0
                ? 'No access pass holders yet.'
                : 'No holders match your search or filters.'}
            </p>
            <p className="subtitle subtitle--dark">
              Issue passes from Access Passes, then return here to print or
              export the roster.
            </p>
          </EmptyContainer>
        </Card>
      ) : (
        <Card body className="card--md">
          <p className="text-muted small mb-3 report-page-header">
            Showing {filteredPasses.length} holder
            {filteredPasses.length === 1 ? '' : 's'}
          </p>
          <div className="table-container border-bottom-0">
            <Table responsive className="mb-0 access-pass-holders-table">
              <thead>
                <tr className="md">
                  <th className="text-left uppercase text-body">Name</th>
                  <th className="text-left uppercase text-body">Email</th>
                  <th className="text-left uppercase text-body">Phone</th>
                  <th className="text-left uppercase text-body">Address</th>
                  <th className="text-left uppercase text-body">Pass</th>
                  <th className="text-left uppercase text-body">Code</th>
                  <th className="text-left uppercase text-body">Events</th>
                  <th className="text-left uppercase text-body">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredPasses.map((pass) => (
                  <tr className="md-x" key={pass.id || pass.uuid}>
                    <td className="text-left fw-medium">
                      {capitalizeString(holderDisplayName(pass))}
                    </td>
                    <td className="text-left">{holderEmail(pass)}</td>
                    <td className="text-left whitespace-nowrap">
                      {holderPhone(pass)
                        ? formatPhoneNumber(holderPhone(pass))
                        : '—'}
                    </td>
                    <td className="text-left">
                      {pass?.holder?.address || pass?.address || '—'}
                    </td>
                    <td className="text-left">{pass?.name || '—'}</td>
                    <td className="text-left">
                      <code className="small">{pass?.checkInCode || '—'}</code>
                    </td>
                    <td className="text-left">{pass?.events?.length || 0}</td>
                    <td className="text-left">
                      <Badge
                        bg={pass?.status === 'active' ? 'success' : 'secondary'}
                      >
                        {capitalizeString(pass?.status || '')}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card>
      )}
    </section>
  )
}
